import { Component, HostListener, Input, OnInit } from '@angular/core';
import { JourneyTipsArrowPosition } from '../../libs/shared/components/journey-tips-popup/journey-tips-popup.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SCREEN_WIDTH_DESKTOP } from '../../constants/app.constant';
import { UsersService } from '../../../features/authentication/services/api/users.service';
import { UpdateUserTutorialInterface } from '../../models/user.model';
import { ErrorService } from '../../services/error.service';
import { MenuTriggerService } from '../header/menu-trigger.service';

@Component({
    selector: 'app-journey-block-ui',
    templateUrl: './journey-block-ui.component.html',
    styleUrl: './journey-block-ui.component.scss',
})
export class JourneyBlockUiComponent implements OnInit {
    @Input() userLastSteps!: JourneyListSequence;
    @Input() hasAaePermission = false;

    protected isFirstStep = false;
    protected isMobileView = false;
    protected isLastStep = false;
    protected isJourneyDetailsReady = false;
    protected currentStep = 1;
    protected journeyDetails: CurrentJourneyDetailsInterface = {} as CurrentJourneyDetailsInterface;

    protected JourneyTipsArrowPosition = JourneyTipsArrowPosition;
    protected JourneyListOrder = JourneyListSequence;

    protected journeyArrayList = [
        JourneyListSequence.Webinars,
        JourneyListSequence.Resources,
        JourneyListSequence.AskAnExpert,
        JourneyListSequence.Search,
        JourneyListSequence.WebinarSchedule,
        JourneyListSequence.Notification,
        JourneyListSequence.MyProfile,
        JourneyListSequence.MyLearning,
        JourneyListSequence.LiveToday,
        JourneyListSequence.ResumeLearning,
    ];

    private targetElement: HTMLElement | null = null;
    private journeyElement: HTMLElement | null = null;

    constructor(
        private activatedRoute: ActivatedRoute,
        private errorService: ErrorService,
        private userService: UsersService,
        private router: Router,
        private menuTriggerService: MenuTriggerService,
    ) {}

    @HostListener('window:resize', ['$event'])
    async onResize(): Promise<void> {
        this.isMobileView = window.innerWidth < SCREEN_WIDTH_DESKTOP;

        await this.setJourneyPopupPosition();

        this.checkIfJourneyPopupOverflow();
    }

    @HostListener('window:scroll', ['$event'])
    async onWindowScroll(): Promise<void> {
        await this.setJourneyPopupPosition();

        this.checkIfJourneyPopupOverflow();
    }

    ngOnInit(): void {
        this.onResize();

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { steps: this.userLastSteps },
            queryParamsHandling: 'merge',
        });

        if (!this.hasAaePermission) {
            this.journeyArrayList = this.journeyArrayList.filter((item) => item !== JourneyListSequence.AskAnExpert);
        }

        this.getStepsFromQueryParam();
    }

    protected async onNextJourney(): Promise<void> {
        const currentIndex = this.journeyArrayList.indexOf(this.journeyDetails.journey);
        const nextIndex = currentIndex + 1;

        if (nextIndex >= this.journeyArrayList.length) {
            this.closeNextJourneyUi();
            return;
        }

        await this.openOrCloseProfileMenu(nextIndex + 1);
        this.updateUserTutorial(this.journeyArrayList[nextIndex]);

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { steps: this.journeyArrayList[nextIndex] },
            queryParamsHandling: 'merge',
        });
    }

    protected async onPrevJourney(): Promise<void> {
        const currentIndex = this.journeyArrayList.indexOf(this.journeyDetails.journey);
        const prevIndex = currentIndex - 1;

        if (prevIndex < 0) {
            return;
        }

        await this.openOrCloseProfileMenu(prevIndex + 1);
        this.updateUserTutorial(this.journeyArrayList[prevIndex]);

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { steps: this.journeyArrayList[prevIndex] },
            queryParamsHandling: 'merge',
        });
    }

    protected closeNextJourneyUi(): void {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { firstTimeJourney: null, steps: null },
            queryParamsHandling: 'merge',
        });

        this.updateUserTutorial(this.journeyArrayList.length, true);
        this.gotoTopScreen();
    }

    private initCourseTipClass(): void {
        switch (this.journeyDetails.journey) {
            case JourneyListSequence.Webinars:
                this.journeyDetails.uiClass = 'tip-webinars';
                this.journeyDetails.arrowPosition = JourneyTipsArrowPosition.TopLeft;
                this.journeyDetails.title = 'journey.webinars.title';
                this.journeyDetails.description = 'journey.webinars.description';
                this.journeyDetails.targetId = 'webinarNav';
                this.journeyDetails.left = 16;
                this.journeyDetails.top = 32;
                this.isFirstStep = true;
                this.isLastStep = false;

                break;
            case JourneyListSequence.Resources:
                this.journeyDetails.uiClass = 'tip-resources';
                this.journeyDetails.arrowPosition = this.isMobileView
                    ? JourneyTipsArrowPosition.TopLeft
                    : JourneyTipsArrowPosition.TopCenter;
                this.journeyDetails.title = 'journey.resources.title';
                this.journeyDetails.description = 'journey.resources.description';
                this.journeyDetails.targetId = 'resourceNav';
                this.journeyDetails.left = this.isMobileView ? 16 : -100;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                break;
            case JourneyListSequence.MyLearning:
                this.journeyDetails.uiClass = 'tip-my-learning';
                this.journeyDetails.arrowPosition = JourneyTipsArrowPosition.TopRight;
                this.journeyDetails.title = 'journey.myLearning.title';
                this.journeyDetails.description = 'journey.myLearning.description';
                this.journeyDetails.targetId = 'myLearningNav';
                this.journeyDetails.left = this.isMobileView ? -10 : -85;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                break;
            case JourneyListSequence.AskAnExpert:
                this.journeyDetails.uiClass = 'tip-ask-an-expert';
                this.journeyDetails.arrowPosition = this.isMobileView
                    ? JourneyTipsArrowPosition.TopLeft
                    : JourneyTipsArrowPosition.TopCenter;
                this.journeyDetails.title = 'journey.askAnExpert.title';
                this.journeyDetails.description = 'journey.askAnExpert.description';
                this.journeyDetails.targetId = 'askAnExpertNav';
                this.journeyDetails.left = this.isMobileView ? 16 : -80;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                break;
            case JourneyListSequence.Search:
                this.journeyDetails.uiClass = 'tip-search';
                this.journeyDetails.arrowPosition = this.isMobileView
                    ? JourneyTipsArrowPosition.TopLeft
                    : JourneyTipsArrowPosition.TopCenter;
                this.journeyDetails.title = 'journey.search.title';
                this.journeyDetails.description = 'journey.search.description';
                this.journeyDetails.targetId = 'globalSearchBar';
                this.journeyDetails.left = this.isMobileView ? 40 : 20;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                break;
            case JourneyListSequence.WebinarSchedule:
                this.journeyDetails.uiClass = 'tip-webinar-schedule';
                this.journeyDetails.arrowPosition = this.isMobileView
                    ? JourneyTipsArrowPosition.TopLeft
                    : JourneyTipsArrowPosition.TopCenter;
                this.journeyDetails.title = 'journey.webinarSchedule.title';
                this.journeyDetails.description = 'journey.webinarSchedule.description';
                this.journeyDetails.targetId = 'webinarScheduleNav';
                this.journeyDetails.left = this.isMobileView ? 0 : -161;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                break;
            case JourneyListSequence.Notification:
                this.journeyDetails.uiClass = 'tip-notification';
                this.journeyDetails.arrowPosition = JourneyTipsArrowPosition.TopRight;
                this.journeyDetails.title = 'journey.notification.title';
                this.journeyDetails.description = 'journey.notification.description';
                this.journeyDetails.targetId = 'notificationNav';
                this.journeyDetails.left = this.isMobileView ? 0 : -300;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                break;
            case JourneyListSequence.MyProfile:
                this.journeyDetails.uiClass = 'tip-my-profile';
                this.journeyDetails.arrowPosition = JourneyTipsArrowPosition.TopRight;
                this.journeyDetails.title = 'journey.myProfile.title';
                this.journeyDetails.description = 'journey.myProfile.description';
                this.journeyDetails.targetId = 'myProfileNav';
                this.journeyDetails.left = 0;
                this.journeyDetails.top = 32;
                this.isFirstStep = false;
                this.isLastStep = false;

                this.gotoTopScreen();

                break;
            case JourneyListSequence.LiveToday:
                this.journeyDetails.uiClass = 'tip-live-today';
                this.journeyDetails.arrowPosition = JourneyTipsArrowPosition.BottomLeft;
                this.journeyDetails.title = 'journey.liveToday.title';
                this.journeyDetails.description = 'journey.liveToday.description';
                this.journeyDetails.targetId = 'liveTodayContainer';
                this.journeyDetails.left = 16;
                this.journeyDetails.top = -175;
                this.isFirstStep = false;
                this.isLastStep = false;

                this.scrollToLiveWebinarSection();

                break;
            case JourneyListSequence.ResumeLearning:
                this.journeyDetails.uiClass = 'tip-resume-learning';
                this.journeyDetails.arrowPosition = JourneyTipsArrowPosition.BottomCenter;
                this.journeyDetails.title = 'journey.resumeLearning.title';
                this.journeyDetails.description = 'journey.resumeLearning.description';
                this.journeyDetails.targetId = 'resumeLearningContainer';
                this.journeyDetails.top = -200;
                this.isFirstStep = false;
                this.isLastStep = true;

                this.gotoTopScreen();

                break;
        }

        this.initCurrentStep();
        this.initJourneyPopup(this.journeyDetails.targetId);
    }

    private updateUserTutorial(steps: JourneyListSequence, isTutorialFinish = this.isLastStep): void {
        const body: UpdateUserTutorialInterface = {
            isTutorialFinish: isTutorialFinish,
            currentTutorialStep: steps,
        };

        this.userService.updateUserTutorial(body).subscribe({
            error: (error) => {
                this.errorService.showError(error);
            },
        });
    }

    private scrollToLiveWebinarSection(): void {
        const element = document.getElementById('liveTodayContainer');

        if (element) {
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - 270;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    }

    private gotoTopScreen(): void {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }

    private getStepsFromQueryParam(): void {
        this.activatedRoute.queryParams.subscribe(async (params) => {
            const firstTimeJourneyParam: JourneyListSequence = params['steps'];

            this.journeyDetails.journey =
                Number(firstTimeJourneyParam) || this.userLastSteps || JourneyListSequence.Webinars;

            await this.openOrCloseProfileMenu(this.journeyDetails.journey);
            this.initCourseTipClass();

            this.isJourneyDetailsReady = true;
        });
    }

    private initJourneyPopup(targetId: string): void {
        this.journeyElement = document.getElementById('journeyContainer');
        let attempts = 0;
        const maxAttempts = 20;
        const retryInterval = 500;

        const checkElement = async (): Promise<void> => {
            const targetElement = document.getElementById(targetId);

            if (targetElement && this.journeyElement && document.getElementById('journeyPointer')) {
                this.targetElement = targetElement;

                await this.setJourneyPopupPosition();

                this.checkIfJourneyPopupOverflow();

                return;
            }

            if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkElement, retryInterval);
            }
        };

        checkElement();
    }

    private setJourneyPopupPosition(): void {
        const journeyPointer = document.getElementById('journeyPointer');

        if (!this.targetElement || !this.journeyElement || !journeyPointer) {
            return;
        }
        const rect = this.targetElement.getBoundingClientRect();

        const topPosition =
            (this.journeyDetails.journey === JourneyListSequence.ResumeLearning ||
            this.journeyDetails.journey === JourneyListSequence.LiveToday
                ? rect.top
                : rect.bottom) + this.journeyDetails.top;
        const leftPosition = rect.left + window.scrollX + (this.journeyDetails.left || 0);

        this.journeyElement.style.position = 'fixed';
        this.journeyElement.style.top = `${topPosition}px`;
        this.journeyElement.style.left = `${leftPosition}px`;

        this.journeyElement.style.transform = `translateX(-5%)`;

        journeyPointer.style.left = `5%`;
    }

    private checkIfJourneyPopupOverflow(): void {
        const journeyPointer = document.getElementById('journeyPointer');

        if (!this.journeyElement || !journeyPointer) {
            return;
        }

        let journeyRect = this.journeyElement.getBoundingClientRect();

        if (journeyRect.right > window.innerWidth) {
            this.journeyElement.style.transform = `translateX(-80%)`;

            journeyPointer.style.left = `80%`;
        }

        journeyRect = this.journeyElement.getBoundingClientRect();

        if (journeyRect.left <= 0) {
            this.journeyElement.style.transform = `translateX(-50%)`;

            journeyPointer.style.left = `50%`;
        }
    }

    private initCurrentStep(): void {
        const index = this.journeyArrayList.indexOf(this.journeyDetails.journey) + 1;

        if (index !== 0) {
            this.currentStep = index;
        }
    }

    private async openOrCloseProfileMenu(index: number): Promise<void> {
        if (index === JourneyListSequence.MyLearning) {
            this.menuTriggerService.openMenu();

            await new Promise((resolve) => setTimeout(resolve, 100));
        } else {
            this.menuTriggerService.closeMenu();
        }
    }
}

export enum JourneyListSequence {
    Webinars = 1,
    Resources = 2,
    AskAnExpert = 3,
    Search = 4,
    WebinarSchedule = 5,
    Notification = 6,
    MyProfile = 7,
    MyLearning = 8,
    LiveToday = 9,
    ResumeLearning = 10,
}

interface CurrentJourneyDetailsInterface {
    journey: JourneyListSequence;
    title: string;
    description: string;
    arrowPosition: JourneyTipsArrowPosition;
    uiClass: string;
    targetId: string;
    top: number;
    left: number;
}
